import { useState, useEffect } from 'react';
import { paymentAPI } from '../../../services/apiService';
import { openRazorpayCheckout } from '../../../utils/razorpayLoader';
import './Wallet.css';

const RechargeModal = ({ onClose, onSuccess, currentBalance, rechargeLimits }) => {
    const [amount, setAmount] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'failed' | null
    const [newBalance, setNewBalance] = useState(null);
    const [gstData, setGstData] = useState(null);
    const [calculatingGst, setCalculatingGst] = useState(false);

    // Get limits from props (fetched from API) or use defaults
    const MIN_AMOUNT = rechargeLimits?.min ?? 5;
    const MAX_AMOUNT = rechargeLimits?.max ?? 1000;

    // Quick amounts - always show standard amounts
    const quickAmounts = [10, 25, 50, 100, 250];

    // Calculate GST when amount changes
    useEffect(() => {
        const calculateGst = async () => {
            const amountValue = parseFloat(amount);
            if (!amountValue || amountValue < MIN_AMOUNT - 0.001) {
                setGstData(null);
                return;
            }

            setCalculatingGst(true);
            try {
                const response = await paymentAPI.calculateGst(amountValue);
                if (response.data.success) {
                    setGstData(response.data.calculation);
                }
            } catch (error) {
                console.error('GST calculation error:', error);
                setGstData(null);
            } finally {
                setCalculatingGst(false);
            }
        };

        const debounceTimer = setTimeout(calculateGst, 300);
        return () => clearTimeout(debounceTimer);
    }, [amount, MIN_AMOUNT]);

    const formatUSD = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(value);
    };

    const handleAmountSelect = (value) => {
        setAmount(value.toString());
        setError('');
    };

    /**
     * Open Razorpay Checkout, then have the server verify what comes back.
     *
     * The browser is never the authority here: it carries a signature the server
     * checks against its own key secret before a cent is credited. And if this
     * never gets to run its verify call — tab closed, connection dropped — the
     * webhook credits the wallet anyway, which is why a failure here is not the
     * same as a lost payment.
     */
    const payWithRazorpay = async (order) => {
        let response;
        try {
            response = await openRazorpayCheckout(order, {
                name: 'Olli',
                description: `Wallet recharge — ${formatUSD(order.amountUsd)}`,
            });
        } catch (e) {
            if (e.dismissed) throw new Error('Payment cancelled');
            throw e;
        }

        // Razorpay handed back a signature, so the payment itself went through.
        // What can still fail from here is OUR call, and the distinction matters
        // to the person reading the screen: telling someone to contact support
        // about a payment the webhook is about to credit is a support ticket we
        // created ourselves.
        let verifyResult;
        try {
            verifyResult = await paymentAPI.verifyRazorpayPayment({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
            });
        } catch (e) {
            throw Object.assign(
                new Error('Payment received — we are still confirming it. Your balance will update in a minute.'),
                { pendingConfirmation: true }
            );
        }

        if (!verifyResult.data.success) {
            throw new Error(verifyResult.data.message || 'We could not verify the payment. Please contact support.');
        }
        return verifyResult.data.data || {};
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const amountValue = parseFloat(amount);
        if (!amountValue || amountValue < MIN_AMOUNT - 0.001) {
            setError(`Minimum recharge amount is ${formatUSD(MIN_AMOUNT)}`);
            return;
        }

        if (amountValue > MAX_AMOUNT) {
            setError(`Maximum recharge amount is ${formatUSD(MAX_AMOUNT)}`);
            return;
        }

        try {
            setLoading(true);
            setError('');

            const response = await paymentAPI.initiatePayment(amountValue);

            if (response.data.success && response.data.data?.razorpayOrderId) {
                try {
                    const result = await payWithRazorpay(response.data.data);
                    setPaymentStatus('success');
                    if (result.newBalance) {
                        setNewBalance(result.newBalance);
                    }
                    // Wait a bit then call onSuccess
                    setTimeout(() => {
                        onSuccess && onSuccess();
                    }, 2000);
                } catch (paymentError) {
                    console.error('Payment error:', paymentError);
                    if (paymentError.message === 'Payment cancelled') {
                        // User cancelled, just show error but don't change status
                        setError('Payment was cancelled');
                    } else if (paymentError.pendingConfirmation) {
                        // Paid, not yet confirmed to us. Not a failure — showing
                        // "Payment failed" here would be false.
                        setError(paymentError.message);
                        setTimeout(() => { onSuccess && onSuccess(); }, 4000);
                    } else {
                        setPaymentStatus('failed');
                        setError(paymentError.message || 'Payment failed');
                    }
                }
            } else {
                setError(response.data.message || 'Failed to initiate payment');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to initiate payment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Show success/failure message
    if (paymentStatus === 'success') {
        return (
            <div className="modal-overlay">
                <div className="recharge-modal" onClick={e => e.stopPropagation()}>
                    <div className="modal-body" style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{ fontSize: '64px', marginBottom: '16px', color: '#10b981' }}>&#10003;</div>
                        <h2 style={{ color: '#10b981', marginBottom: '8px' }}>Payment Successful!</h2>
                        <p style={{ color: '#6b7280' }}>Your wallet has been recharged with {formatUSD(parseFloat(amount))}</p>
                        {newBalance !== null && (
                            <p style={{ color: '#374151', marginTop: '8px', fontWeight: '600' }}>
                                New Balance: {formatUSD(newBalance)}
                            </p>
                        )}
                        <button
                            className="btn-primary"
                            onClick={() => onSuccess && onSuccess()}
                            style={{ marginTop: '24px' }}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="recharge-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Recharge Wallet</h2>
                    <button className="modal-close" onClick={onClose}>&times;</button>
                </div>

                <div className="modal-body">
                    {/* Current Balance */}
                    <div className="current-balance-card">
                        <span className="label">Current Balance</span>
                        <span className="balance">{formatUSD(currentBalance)}</span>
                    </div>

                    {error && <div className="error-message">{error}</div>}

                    {/* Quick Amount Selection */}
                    <div className="quick-amounts">
                        <label>Quick Select</label>
                        <div className="amount-buttons">
                            {quickAmounts.map(value => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`amount-btn ${amount === value.toString() ? 'active' : ''}`}
                                    onClick={() => handleAmountSelect(value)}
                                >
                                    {formatUSD(value)}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Amount */}
                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>Enter Amount (USD)</label>
                            <div className="amount-input-wrapper">
                                <span className="currency-symbol">$</span>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => {
                                        setAmount(e.target.value);
                                        setError('');
                                    }}
                                    placeholder="Enter amount"
                                    min={MIN_AMOUNT}
                                    max={MAX_AMOUNT}
                                    step="0.01"
                                    required
                                />
                            </div>
                            <span className="form-hint">Min: {formatUSD(MIN_AMOUNT)} | Max: {formatUSD(MAX_AMOUNT)}</span>
                        </div>

                        {/* GST Breakdown - Show for all users, but GST only for Indian orgs */}
                        {gstData && (
                            <div className="gst-breakdown">
                                <div className="breakdown-row">
                                    <span>Recharge Amount:</span>
                                    <span>{formatUSD(gstData.baseAmount)}</span>
                                </div>
                                {gstData.isIndian && gstData.gstAmount > 0 && (
                                    <div className="breakdown-row gst">
                                        <span>GST ({gstData.gstRate}%):</span>
                                        <span>{formatUSD(gstData.gstAmount)}</span>
                                    </div>
                                )}
                                <div className="breakdown-row total">
                                    <span>Total to Pay:</span>
                                    <span>{formatUSD(gstData.totalAmount)}</span>
                                </div>
                                {gstData.isIndian && (
                                    <p className="gst-note">
                                        * GST applicable for Indian organizations
                                    </p>
                                )}
                            </div>
                        )}

                        {/* New Balance Preview */}
                        {amount && parseFloat(amount) >= MIN_AMOUNT - 0.001 && (
                            <div className="new-balance-preview">
                                <span className="label">New Balance after Recharge</span>
                                <span className="balance">{formatUSD(currentBalance + parseFloat(amount))}</span>
                            </div>
                        )}

                        {/* Payment Info */}
                        <div className="payment-info">
                            <p>Secure payment via Razorpay</p>
                            <div className="payment-methods">
                                <span>Accepted: UPI, Cards, Net Banking, Wallets</span>
                            </div>
                        </div>

                        <div className="modal-footer">
                            <button type="button" className="btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={loading || !amount || isNaN(parseFloat(amount)) || parseFloat(amount) < MIN_AMOUNT - 0.001 || calculatingGst}
                            >
                                {loading ? 'Processing...' :
                                    calculatingGst ? 'Calculating...' :
                                    `Pay ${gstData ? formatUSD(gstData.totalAmount) : (amount && !isNaN(parseFloat(amount)) ? formatUSD(parseFloat(amount)) : '')}`}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RechargeModal;
